import { addResources, payResources } from "../resources/resource-pool";
import { getCardInstance, getEventDefinitionForInstance } from "../selectors/card-instances";
import { getEventLimitSnapshot } from "../selectors/event-limit";
import type { GameEvent, GameStateV5, PlayEventAction } from "../types";
import type { TransitionResult } from "./advance-phase";
import { drawCards } from "./draw-cards";

function eventId(actionSequence: number, ordinal: number): string {
  return `event-${actionSequence}-${ordinal}`;
}

export function applyPlayEvent(
  state: Readonly<GameStateV5>,
  action: PlayEventAction,
  actionSequence: number,
): TransitionResult {
  const instance = getCardInstance(state, action.cardInstanceId);
  const definition = getEventDefinitionForInstance(state, action.cardInstanceId);
  if (instance === undefined || definition === undefined) {
    throw new Error("Validated Event play lost its schema-4 definition.");
  }

  const player = state.players[action.playerId];
  const limit = getEventLimitSnapshot(state, action.playerId);
  const costPaid = { ...definition.cost };
  let current: GameStateV5 = {
    ...state,
    players: {
      ...state.players,
      [action.playerId]: {
        ...player,
        hand: player.hand.filter((cardInstanceId) => cardInstanceId !== action.cardInstanceId),
        eventResolution: [...player.eventResolution, action.cardInstanceId],
        kingdomPool: payResources(player.kingdomPool, definition.cost),
        actionsUsed: {
          ...player.actionsUsed,
          eventsPlayed: player.actionsUsed.eventsPlayed + 1,
        },
      },
    },
  };
  const events: GameEvent[] = [
    {
      type: "event-played",
      eventId: eventId(actionSequence, 0),
      actionSequence,
      playerId: action.playerId,
      cardInstanceId: action.cardInstanceId,
      cardDefinitionId: instance.cardDefinitionId,
      costPaid,
      eventLimit: limit.limit,
      eventsPlayed: limit.eventsPlayed + 1,
    },
  ];

  for (let effectIndex = 0; effectIndex < definition.effects.length; effectIndex += 1) {
    const effect = definition.effects[effectIndex];
    if (effect === undefined) continue;
    events.push({
      type: "event-effect-resolved",
      eventId: eventId(actionSequence, events.length),
      actionSequence,
      playerId: action.playerId,
      cardInstanceId: action.cardInstanceId,
      effectIndex,
      effect:
        effect.type === "gain-kingdom-resources"
          ? { ...effect, resources: { ...effect.resources } }
          : { ...effect },
    });

    if (effect.type === "draw-cards") {
      const draw = drawCards(current, action.playerId, effect.count, actionSequence, events.length);
      if (draw.state.schemaVersion !== 5) {
        throw new Error("Public-beta Event draw changed the state schema.");
      }
      current = draw.state;
      events.push(...draw.events);
    } else {
      const currentPlayer = current.players[action.playerId];
      current = {
        ...current,
        players: {
          ...current.players,
          [action.playerId]: {
            ...currentPlayer,
            kingdomPool: addResources(currentPlayer.kingdomPool, effect.resources),
          },
        },
      };
    }
  }

  const resolvedPlayer = current.players[action.playerId];
  current = {
    ...current,
    players: {
      ...current.players,
      [action.playerId]: {
        ...resolvedPlayer,
        eventResolution: resolvedPlayer.eventResolution.filter(
          (cardInstanceId) => cardInstanceId !== action.cardInstanceId,
        ),
        discard: [...resolvedPlayer.discard, action.cardInstanceId],
      },
    },
  };
  events.push({
    type: "event-moved-to-discard",
    eventId: eventId(actionSequence, events.length),
    actionSequence,
    playerId: action.playerId,
    cardInstanceId: action.cardInstanceId,
  });
  return { state: current, events };
}
